// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { quantityInputStyle, QuantityInputStyles } from "@flip/App/SharedStyle/QuantityInputStyle";
import { xMask } from "@flip/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";

import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import setBudgetPeriodType from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/SetBudgetPeriodType";
import setCustomBudgetPeriodNumber from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/SetCustomBudgetPeriodNumber";
import translate from "@insite/client-framework/Translate";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { Clickable, Icon } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    labelOverride?: React.ReactNode;
    extendedStyles?: QuantityInputStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        customBudgetPeriodNumber: state.pages.budgetManagement.customBudgetPeriodNumber,
        budgetPeriodType: state.pages.budgetManagement.budgetPeriodType,
    };
};
const mapDispatchToProps = {
    setBudgetPeriodType,
};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ConfigureBudgetPeriodStepper = ({
    labelOverride,
    extendedStyles,
    customBudgetPeriodNumber,
    budgetPeriodType,
    setBudgetPeriodType,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(quantityInputStyle, extendedStyles));
    const [budgetPeriod, setBudgetPeriod] = useState(customBudgetPeriodNumber);

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    // XNG-Changes: apply qty mask for value
    const updateBudgetPeriodAction = (type: "increase" | "decrease") => {
        const event = { target: { value: `${Number(budgetPeriod) + (type === "increase" ? 1 : -1)}` } } as any;
        handleCustomBudgetPeriodNumberChange(event);
    };

    const handleCustomBudgetPeriodNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            setCustomBudgetPeriodNumber({ customBudgetPeriodNumber: undefined });
            return;
        }
        let value = Number(event.target.value);
        value = value > 13 ? 13 : value;
        value = value < 1 ? 1 : value;
        setBudgetPeriod(value);
        setBudgetPeriodType({
            budgetPeriodType,
            customBudgetPeriodNumber: value,
        });
    };
    return (
        <StyledWrapper {...styles?.wrapper}>
            <Clickable
                {...styles?.actionClickable}
                disabled={Number(budgetPeriod) <= 1}
                onClick={() => updateBudgetPeriodAction("decrease")}
                data-test-selector="custom_budget_period_decrease"
                aria-label="decrease period"
            >
                <Icon src={Minus} size={12} />
            </Clickable>
            {/* <VisuallyHidden as="label" htmlFor={textFieldId}>
                {translate("QTY")}
            </VisuallyHidden> */}
            {/* <TextField
                label={labelOverride ?? translate("QTY")}
                id={textFieldId}
                value={budgetPeriod}
                type="number"
                min={0}
                max={999999}
                maxLength={6}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                {...styles?.quantityOrderedTextField}
            /> */}
            <TextField
                // label={translate("Period")}
                value={budgetPeriod || ""}
                {...styles?.quantityOrderedTextField}
                type="qty"
                min={1}
                max={99}
                maxLength={2}
                data-test-selector="custom_Budget_Period_Number"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={handleCustomBudgetPeriodNumberChange}
            />
            <Clickable
                {...styles?.actionClickable}
                disabled={Number(budgetPeriod) >= 99}
                onClick={() => updateBudgetPeriodAction("increase")}
                data-test-selector="custom_budget_period_increase"
                aria-label="increase period"
            >
                <Icon src={Plus} size={12} />
            </Clickable>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigureBudgetPeriodStepper);
